import React, { useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { PrimaryButton } from 'office-ui-fabric-react';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import PageTitle from '../../components/PageTitle';
import StackGrid from '../../components/StackGrid';
import SubModule from '../../components/SubModule';
import styles from './AddPartner.style';
import SubModuleContent from '../../components/SubModuleContent';
import TextField from '../../components/TextField';
import TextButton from '../../components/TextButton';
import SavingIndicator from '../../components/SavingIndicator';
import Main from '../../components/Main';
import { createPartner } from '../../actions/partners';

const AddPartner = () => {
  const [isSaving, setIsSaving] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  const partnerSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    partnerRole: Yup.string().required('Required'),
    partnerAddress: Yup.string().required('Required'),
  });
  const initialFormValues = {
    name: '',
    partnerRole: '',
    partnerAddress: '',
  };

  const savePartner = async (formValues, { setErrors }) => {
    setIsSaving(true);
    const response = await dispatch(createPartner(formValues));

    // Display error messages from api , if any, on the UI
    if (response.error && response.payload.message) {
      const error = new Error(response.payload.message);
      error.payload = response.payload.message;
      setErrors({
        form: error.message,
        duplicatePartnerAddress: error.message.includes('address already exists'),
      });
      setIsSaving(false);
      return;
    }
    setIsSaving(false);
    history.push(`/partners`);
  };

  const regeneratePartnerAddress = setFieldValue => {
    setFieldValue('partnerAddress', '');
  };

  return (
    <>
      <PageTitle>Create Partner</PageTitle>
      <Main>
        <Formik
          initialValues={initialFormValues}
          onSubmit={savePartner}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={partnerSchema}
        >
          {({ errors, setFieldValue, submitForm }) => (
            <Form data-testid="addPartner">
              {errors.form && (
                <MessageBar
                  messageBarType={MessageBarType.error}
                  isMultiline
                  dismissButtonAriaLabel="Close"
                  nowrap
                >
                  {errors.form}
                </MessageBar>
              )}
              <Module>
                <ModuleContent>
                  <SubModule disableAccordian>
                    <SubModuleContent>
                      <StackGrid smCols={1} mdCols={2} lgCols={2} shrink>
                        <Field
                          name="name"
                          label="Name"
                          component={TextField}
                          disabled={isSaving}
                          required
                        />
                        <Field
                          name="partnerRole"
                          label="Role"
                          component={TextField}
                          disabled={isSaving}
                          required
                        />
                        <Field
                          name="partnerAddress"
                          label="Address"
                          component={TextField}
                          disabled={isSaving}
                          required
                        />
                        {errors.form && errors.duplicatePartnerAddress && (
                          <TextButton
                            className={styles.regenerateButton}
                            onClick={() => regeneratePartnerAddress(setFieldValue)}
                            data-testid="select-all"
                          >
                            {' '}
                            Regenerate address
                          </TextButton>
                        )}
                      </StackGrid>
                    </SubModuleContent>
                  </SubModule>
                </ModuleContent>
              </Module>

              <div className={styles.buttonContainer}>
                <TextButton
                  className={styles.cancelButton}
                  onClick={() => history.push(`/partners`)}
                >
                  Cancel
                </TextButton>
                {!isSaving ? (
                  <PrimaryButton
                    className={styles.saveDraftButton}
                    disabled={isSaving}
                    onClick={submitForm}
                    data-testid="savePartnerButton"
                  >
                    Save
                  </PrimaryButton>
                ) : (
                  <SavingIndicator data-testid="saveLoader" className={styles.saveDraftButton} />
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Main>
    </>
  );
};

export default AddPartner;
